import { useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import Paginator from "../exercise/components/Paginator";
import NewAssignment from "../new_assignment/NewAssignment";
import Button from "../../components/Button";
import ExitButton from "../../components/ExitButton";
import authFetch from "../../api/authFetch";

const ClassInterface = ({successHandler, course, teacher = ""}) => {
    const {auth, setAuth} = useAuth();
    const [students, setStudents] = useState([]);
    const [studentPages, setStudentPages] = useState(0);
    const [studentPage, setStudentPage] = useState(1);
    const [assignments, setAssignments] = useState([]);
    const [chats, setChats] = useState([]);
    const [chat, setChat] = useState("");
    const [changeList, setChangeList] = useState([]);
    const [student, setStudent] = useState("");
    const [assignmentPage, setNewAssign] = useState(false);
    const [tagError, setTagError] = useState(null);

    const getTeacher = () => {
        return course.split("/")[0];
    }

    const getTag = () => {
        return course.split("/")[1];
    }

    const isTeacher = auth?.username === getTeacher() || teacher === getTeacher();
    const tag = getTag();

    const [newTag, setNewTag] = useState(tag);

    const fetchData = async (route, method) => {
        const response = await authFetch(`/teacher/${route}` + ((teacher !== "") ? `?teacher_name=${teacher}` : ""), {
            method: method
        });
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
            return null;
        }
        if (response.ok) {
            const json = await response.json();
            return json;
        }
        return null;
    }

    const getStudents = async (page = 1) => {
        const response = await authFetch(`/course/students/${course}/${page}`, {
            method: "GET"
        });
        if (response.ok) {
            const json = await response.json();
            setStudents(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const leaveClass = async () => {
        const response = await authFetch(`/users/leave-class/${course}`, {
            method: "DELETE"
        });
        if (response.ok) {
            successHandler("");
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }
    
    const getStudentPages = async () => {
        const response = await authFetch(`/course/student-pages/${course}`, {
            method: "GET"
        });
        if (response.ok) {
            const json = await response.json();
            setStudentPages(json.pages);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const getAssignments = async () => {
        const response = await authFetch(`/course/assignments/${course}`, {
            method: "GET"
        });
        if (response.ok) {
            const json = await response.json();
            setAssignments(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const getChats = async () => {
        const response = await authFetch(`/course/chats/${course}`, {
            method: "GET"
        });
        if (response.ok) {
            const json = await response.json();
            setChats(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const sendChat = async () => {
        const response = await authFetch(`/course/chat/${course}`, {
            method: "POST",
            body: JSON.stringify({text: chat})
        });
        if (response.ok) {
            getChats();
            setChat("");
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const checkChat = async () => {
        const response = await authFetch(`/course/chat-count/${course}`, {
            method: "GET"
        });
        if (response.ok) {
            const json = await response.json();
            if (json.count !== chats.length) {
                getChats();
            }
        }
    }

    const addChange = (change) => {
        setChangeList([...changeList, change]);
    }

    const removeChange = (i) => {
        let array = [...changeList];
        array.splice(i, 1);
        setChangeList(array);
    }

    const addAddStudent = () => {
        addChange(`ADD ${student}`);
        setStudent("");
    }

    const addRemoveStudent = (name) => {
        addChange(`REMOVE ${name}`);
    }

    const addDeleteClass = () => {
        addChange("DELETE");
    }

    const addUpdateTag = () => {
        addChange(`RENAME ${newTag}`);
    }

    const addStudent = async (name) => {
        await fetchData(`add-user/${tag}/${name}`, "POST");
    }

    const removeStudent = async (name) => {
        await fetchData(`remove-user/${tag}/${name}`, "DELETE");
    }

    const deleteClass = async () => {
        await fetchData(`delete-class/${tag}`, "DELETE");
    }

    const updateTag = async (t) => {
        await fetchData(`update-tag/${tag}/${t}`, "POST");
    }

    const commit = async () => {
        for (const change of changeList) {
            const splits = change.split(" ");
            switch (splits[0]) {
                case "ADD":
                    await addStudent(splits[1]);
                    break;
                case "REMOVE":
                    await removeStudent(splits[1]);
                    break;
                case "DELETE":
                    await deleteClass();
                    break;
                case "RENAME":
                    await updateTag(splits[1]);
                    break;
            }
        }
        setChangeList([]);
        getStudents();
        getAssignments();
    }

    const newAssignHandler = (state) => {
        setNewAssign(state);
        getAssignments();
    }

    const isTagChanged = (t = newTag) => {
        return t !== tag && t !== "";
    }

    const getTagError = async (t = newTag) => {
        if (t === "") {
            return "Name of class is required.";
        }
        for (let c of [" ", "/"]) {
            if (t.includes(c)) {
                return `Name of class cannot include \"${c}\".`;
            }
        }
        const json = await fetchData(`tag-exists/${t}`, "GET");
        if (json.exists && t !== tag) {
            return "You already have a class with this name";
        }
        return null;
    }

    const changeNewTag = async (t) => {
        setNewTag(t);
        setTagError(null);
        const err = await getTagError(t);
        if (err) {
            setTagError(err);
        }
    }
    
    const canUpdateTag = () => {
        return isTagChanged() && !tagError;
    }

    useEffect(() => {getStudents()}, []);
    useEffect(() => {getStudentPages()}, []);
    useEffect(() => {getStudents(studentPage)}, [studentPage]);
    useEffect(() => {getAssignments()}, []);
    useEffect(() => {getChats()}, []);
    useEffect(() => {checkChat()});

    if (assignmentPage) {
        return <NewAssignment successHandler={state => newAssignHandler(state)} tag={tag} teacher={teacher} />
    }

    return <main className="page-shell">
        <div className="page-container">
        <div className="mb-6 flex items-center gap-4">
            <ExitButton func={() => {successHandler("")}} />
            <div className="min-w-0">
                <p className="panel-heading">{isTeacher ? "Class workspace" : "Your class"}</p>
                <h1 className="page-heading truncate">{tag}</h1>
            </div>
        </div>
        <div className="flex flex-col">
            {isTeacher ? <form
                className="panel flex flex-wrap items-start gap-3 p-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (canUpdateTag()) {
                        addUpdateTag();
                    }
                }}
            >
                <div className="flex min-w-[14rem] flex-1 flex-col">
                    <label className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Class name</label>
                    <input className="form-field" type="text" value={newTag} onChange={e => changeNewTag(e.target.value)} />
                    {tagError && <div className="text-red-500">{tagError}</div>}
                </div>
                {canUpdateTag() && <Button text="Update" type="submit" />}
            </form> : null}
            <div className="class-grid">
                <section className="panel class-panel">
                    <div className="flex items-center justify-between">
                        <h2 className="panel-heading">Students</h2>
                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-bold text-cyan-800">{students.length}</span>
                    </div>
                        <Paginator maxpage={studentPages} 
                            page={studentPage}
                            clickHandler={page => setStudentPage(page)}
                        />
                        <div className="flex flex-col gap-2">
                        {students.map(studentName => <div className="list-row" key={studentName}>
                            <div className="min-w-0 truncate font-semibold">{studentName}</div>
                            {isTeacher && <Button text="Remove" func={() => addRemoveStudent(studentName)} variant="ghost" />}
                        </div>)}
                        {students.length === 0 && <div className="empty-state">No students in this class yet.</div>}
                        </div>
                    {isTeacher && <form
                        className="mt-auto flex flex-col gap-2 sm:flex-row"
                        onSubmit={(event) => {
                            event.preventDefault();
                            addAddStudent();
                        }}
                    >
                        <input
                            className="form-field min-w-0 flex-1"
                            type="text"
                            placeholder="Username/email"
                            value={student}
                            onChange={e => setStudent(e.target.value)}
                        />
                        <Button text="Invite" type="submit" />
                    </form>}
                </section>
                <section className="panel class-panel">
                    <div className="flex items-center justify-between">
                        <h2 className="panel-heading">Assignments</h2>
                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-bold text-cyan-800">{assignments.length}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        {assignments.map(assignment => <div className="list-row font-semibold" key={assignment}>{assignment}</div>)}
                        {assignments.length === 0 && <div className="empty-state">No assignments have been posted.</div>}
                    </div>
                    {isTeacher && <Button className="mt-auto" text="New assignment" func={() => setNewAssign(true)} />}
                </section>
                <section className="panel class-panel">
                    <h2 className="panel-heading">Class chat</h2>
                    <div className="chat-list">
                    {chats.map((chatMessage, index) => <div className="chat-bubble" key={`${chatMessage.user}-${chatMessage.time}-${index}`}>
                        <div className="text-xs font-bold text-cyan-900">{chatMessage.user}</div>
                        <div className="my-1 text-sm text-slate-700">{chatMessage.text}</div>
                        <div className="text-[0.7rem] text-slate-400">{chatMessage.time}</div>
                    </div>)}
                    {chats.length === 0 && <div className="empty-state">Start the conversation.</div>}
                    </div>
                    <form
                        className="mt-auto flex gap-2"
                        onSubmit={(event) => {
                            event.preventDefault();
                            sendChat();
                        }}
                    >
                        <input
                            className="form-field min-w-0 flex-1"
                            type="text"
                            value={chat}
                            onChange={(e) => setChat(e.target.value)}
                        />
                        <Button text="Send" type="submit" />
                    </form>
                </section>
            </div>
            {isTeacher && <div>
                {(changeList?.length>0) && <div className="panel my-5 flex flex-col gap-3 p-5">
                    <h2 className="panel-heading">Pending changes</h2>
                    {changeList.map((change, i) => <div className="list-row" key={`${change}-${i}`}>
                        <div className="text-sm font-semibold text-slate-600">{change}</div>
                        <Button text="Remove" func={() => removeChange(i)} variant="ghost"/>
                    </div>)}
                    <Button className="self-end" text="Apply changes" func={commit} />
                </div>}
            </div>}
        </div>
        <div className="mt-8 flex justify-end border-t border-slate-200 pt-6">
            {isTeacher ? <Button
                text="Delete class"
                func={addDeleteClass}
                variant="danger"
            /> : <Button
                text="Leave class"
                func={leaveClass}
                variant="danger"
            />}
        </div>
        </div>
    </main>
}

export default ClassInterface;
